import { lazy, type ComponentType } from 'react';

// Pages are code-split, so opening one for the first time fetches its chunk over
// the network. On a site with patchy signal that fetch can fail on a blip that
// would have succeeded a second later. React.lazy caches a rejected import
// forever, so the retry has to happen here, before the promise reaches lazy() —
// once it rejects, only a full reload recovers that page.

const RETRIES = 2;
const BACKOFF_MS = 400;

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export function lazyPage<T extends ComponentType<unknown>>(load: () => Promise<{ default: T }>) {
  return lazy(async () => {
    for (let attempt = 0; ; attempt++) {
      try {
        return await load();
      } catch (error) {
        // A deploy that landed mid-session leaves the browser asking for chunk
        // hashes that no longer exist. Retrying cannot fix that, but it costs
        // under a second and it rescues the far more common signal drop.
        if (attempt >= RETRIES) throw error;
        await wait(BACKOFF_MS * 2 ** attempt);
      }
    }
  });
}
